import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";

type FeaturePayloadState = string | boolean | number | Record<string, unknown> | unknown[];
type FeatureFlagState = boolean | string;

/**
 * FeatureDataStateService
 * --------------------------------------------------------------------
 * Per-feature payload, edit-tracking, and input-error streams that
 * were previously declared inline on `ApiService`.
 *
 * NOTE on typing: streams use broad unions to preserve legacy payload
 * flexibility while removing raw untyped contracts.
 */
@Injectable({ providedIn: "root" })
export class FeatureDataStateService {
  // --- Per-feature data payloads ------------------------------------
  readonly corridordata = new BehaviorSubject<FeaturePayloadState>("");
  readonly getCorridorData: Observable<FeaturePayloadState> = this.corridordata.asObservable();

  readonly corridorgraphdata = new BehaviorSubject<FeaturePayloadState>("");
  readonly getCorridorGraphData: Observable<FeaturePayloadState> = this.corridorgraphdata.asObservable();

  readonly mtpgraphdata = new BehaviorSubject<FeaturePayloadState>("");
  readonly getMtpGraphData: Observable<FeaturePayloadState> = this.mtpgraphdata.asObservable();

  readonly mtpdata = new BehaviorSubject<FeaturePayloadState>("");
  readonly getMtpData: Observable<FeaturePayloadState> = this.mtpdata.asObservable();

  readonly ztvoutputdata = new BehaviorSubject<FeaturePayloadState>("");
  readonly getztvoutputData: Observable<FeaturePayloadState> = this.ztvoutputdata.asObservable();

  readonly rsodata = new BehaviorSubject<FeaturePayloadState>("");
  readonly getrsoData: Observable<FeaturePayloadState> = this.rsodata.asObservable();

  readonly lipdata = new BehaviorSubject<FeaturePayloadState>("");
  readonly getlipData: Observable<FeaturePayloadState> = this.lipdata.asObservable();

  readonly diia253maintenancedata = new BehaviorSubject<FeaturePayloadState>("");

  readonly ampDimdata = new BehaviorSubject<FeaturePayloadState>("");
  readonly getAmpDimData: Observable<FeaturePayloadState> = this.ampDimdata.asObservable();

  readonly ampDimGraphdata = new BehaviorSubject<FeaturePayloadState>("");
  readonly getAmpDimGraphdata: Observable<FeaturePayloadState> = this.ampDimGraphdata.asObservable();

  readonly lineswitchdata = new BehaviorSubject<FeaturePayloadState>("");
  readonly getLineSwitchData: Observable<FeaturePayloadState> = this.lineswitchdata.asObservable();

  readonly codedmainsdata = new BehaviorSubject<FeaturePayloadState>("");
  readonly getcodedmainsData: Observable<FeaturePayloadState> = this.codedmainsdata.asObservable();

  readonly owpdata = new BehaviorSubject<FeaturePayloadState>("");
  readonly getowpData: Observable<FeaturePayloadState> = this.owpdata.asObservable();

  // --- Edit-tracking ------------------------------------------------
  readonly corridorEditValueAdd = new BehaviorSubject<FeatureFlagState>(false);
  readonly getCorridorEdtedData: Observable<FeatureFlagState> = this.corridorEditValueAdd.asObservable();

  readonly mtpEditValueAdd = new BehaviorSubject<FeatureFlagState>(false);
  readonly getMtpEdtedData: Observable<FeatureFlagState> = this.mtpEditValueAdd.asObservable();

  readonly ztvoutputEditValueAdd = new BehaviorSubject<FeatureFlagState>(false);
  readonly getztvoutputEdtedData: Observable<FeatureFlagState> = this.ztvoutputEditValueAdd.asObservable();

  readonly lipEditValueAdd = new BehaviorSubject<FeatureFlagState>(false);
  readonly getlipEdtedData: Observable<FeatureFlagState> = this.lipEditValueAdd.asObservable();

  readonly rsoEditValueAdd = new BehaviorSubject<FeatureFlagState>(false);
  readonly getrsoEdtedData: Observable<FeatureFlagState> = this.rsoEditValueAdd.asObservable();

  readonly dali102EditValueAdd = new BehaviorSubject<FeatureFlagState>(false);
  readonly getDali102EdtedData: Observable<FeatureFlagState> = this.dali102EditValueAdd.asObservable();

  readonly diia253maintenanceEditValueAdd = new BehaviorSubject<FeatureFlagState>(false);

  readonly lineSwitchEditValueAdd = new BehaviorSubject<FeatureFlagState>(false);
  readonly getLineSwitchEdtedData: Observable<FeatureFlagState> = this.lineSwitchEditValueAdd.asObservable();

  readonly owpEditValueAdd = new BehaviorSubject<FeatureFlagState>(false);
  readonly codedmainsEditValueAdd = new BehaviorSubject<FeatureFlagState>(false);
  readonly getcodedmainsEdtedData: Observable<FeatureFlagState> = this.codedmainsEditValueAdd.asObservable();

  // --- Per-feature input-error flags --------------------------------
  readonly codedmainsError = new BehaviorSubject<FeatureFlagState>(false);
  readonly getcodedmainsError: Observable<FeatureFlagState> = this.codedmainsError.asObservable();

  readonly owpError = new BehaviorSubject<FeatureFlagState>(false);
  readonly getowpError: Observable<FeatureFlagState> = this.owpError.asObservable();

  readonly lineSwitchError = new BehaviorSubject<FeatureFlagState>(false);
  readonly getLineSwitchError: Observable<FeatureFlagState> = this.lineSwitchError.asObservable();

  readonly corridorError = new BehaviorSubject<FeatureFlagState>(false);
  readonly getCorridorError: Observable<FeatureFlagState> = this.corridorError.asObservable();

  readonly mtpError = new BehaviorSubject<FeatureFlagState>(false);
  readonly getMtpError: Observable<FeatureFlagState> = this.mtpError.asObservable();

  readonly ztvoutputError = new BehaviorSubject<FeatureFlagState>(false);
  readonly getztvoutputError: Observable<FeatureFlagState> = this.ztvoutputError.asObservable();

  readonly rsoError = new BehaviorSubject<FeatureFlagState>(false);
  readonly getrsoError: Observable<FeatureFlagState> = this.rsoError.asObservable();

  readonly lipError = new BehaviorSubject<FeatureFlagState>(false);
  readonly getlipError: Observable<FeatureFlagState> = this.lipError.asObservable();

  readonly diia253maintenanceError = new BehaviorSubject<FeatureFlagState>(false);

  readonly ampdimError = new BehaviorSubject<FeatureFlagState>(false);
  readonly getampdimError: Observable<FeatureFlagState> = this.ampdimError.asObservable();

  // --- Data setters -------------------------------------------------
  sendMtpdata(data: FeaturePayloadState): void {
    this.mtpdata.next(data);
  }
  sendztvoutputdata(data: FeaturePayloadState): void {
    this.ztvoutputdata.next(data);
  }
  sendrsodata(data: FeaturePayloadState): void {
    this.rsodata.next(data);
  }
  sendlipdata(data: FeaturePayloadState): void {
    this.lipdata.next(data);
  }
  senddiia253maintenancedata(data: FeaturePayloadState): void {
    this.diia253maintenancedata.next(data);
  }
  sendAmpDimdata(data: FeaturePayloadState): void {
    this.ampDimdata.next(data);
  }
  sendAmpDimGraphdata(data: FeaturePayloadState): void {
    this.ampDimGraphdata.next(data);
  }
  sendMtpGraphdata(data: FeaturePayloadState): void {
    this.mtpgraphdata.next(data);
  }
  sendCorridordata(data: FeaturePayloadState): void {
    this.corridordata.next(data);
  }
  sendCorridorGraphdata(data: FeaturePayloadState): void {
    this.corridorgraphdata.next(data);
  }
  sendLineSwitchdata(data: FeaturePayloadState): void {
    this.lineswitchdata.next(data);
  }
  sendcodedmainsdata(data: FeaturePayloadState): void {
    this.codedmainsdata.next(data);
  }
  sendowpdata(data: FeaturePayloadState): void {
    this.owpdata.next(data);
  }

  // --- Edit-tracking setters ----------------------------------------
  corridorEditedVal(data: FeatureFlagState): void {
    this.corridorEditValueAdd.next(data);
  }
  mtpEditedVal(data: FeatureFlagState): void {
    this.mtpEditValueAdd.next(data);
  }
  ztvoutputEditedVal(data: FeatureFlagState): void {
    this.ztvoutputEditValueAdd.next(data);
  }
  lipEditedVal(data: FeatureFlagState): void {
    this.lipEditValueAdd.next(data);
  }
  rsoEditedVal(data: FeatureFlagState): void {
    this.rsoEditValueAdd.next(data);
  }
  dali102EditedVal(data: FeatureFlagState): void {
    this.dali102EditValueAdd.next(data);
  }
  diia253maintenanceEditedVal(data: FeatureFlagState): void {
    this.diia253maintenanceEditValueAdd.next(data);
  }
  lineSwitchEditedVal(data: FeatureFlagState): void {
    this.lineSwitchEditValueAdd.next(data);
  }
  owpEditedVal(data: FeatureFlagState): void {
    this.owpEditValueAdd.next(data);
  }
  codedmainsEditedVal(data: FeatureFlagState): void {
    this.codedmainsEditValueAdd.next(data);
  }

  // --- Error setters ------------------------------------------------
  mtpInputError(data: FeatureFlagState): void {
    this.mtpError.next(data);
  }
  ztvoutputInputError(data: FeatureFlagState): void {
    this.ztvoutputError.next(data);
  }
  rsoInputError(data: FeatureFlagState): void {
    this.rsoError.next(data);
  }
  lipInputError(data: FeatureFlagState): void {
    this.lipError.next(data);
  }
  diia253maintenanceInputError(data: FeatureFlagState): void {
    this.diia253maintenanceError.next(data);
  }
  ampdimInputError(data: FeatureFlagState): void {
    this.ampdimError.next(data);
  }
  corridorInputError(data: FeatureFlagState): void {
    this.corridorError.next(data);
  }
  lineswitchInputError(data: FeatureFlagState): void {
    this.lineSwitchError.next(data);
  }
  codedmainsInputError(data: FeatureFlagState): void {
    this.codedmainsError.next(data);
  }
  owpInputError(data: FeatureFlagState): void {
    this.owpError.next(data);
  }
}
